import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Reveal, Eyebrow, GhostButton } from "@/components/site/ui";
import { ArrowLeft, ExternalLink } from "lucide-react";

// Legacy articles stored plain text; wrap them so line breaks survive as HTML.
function renderBody(body) {
  const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(body);
  if (looksLikeHtml) return body;
  return body
    .split(/\n{2,}/)
    .map((p) => `<p>${p.replace(/\n/g, "<br/>")}</p>`)
    .join("");
}


export default function NewsArticle() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/news/${id}`).then(({ data }) => setArticle(data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="pt-40 pb-32 text-center" data-testid="article-not-found">
        <p className="font-heading text-3xl text-ink">Story not found</p>
        <div className="mt-6 flex justify-center"><GhostButton to="/">Back to Home</GhostButton></div>
      </div>
    );
  }

  if (!article) {
    return <div className="pt-48 pb-32 text-center font-body text-ink/50">Loading…</div>;
  }

  return (
    <article data-testid="news-article-page" className="pt-32 md:pt-40 pb-24">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <Reveal>
          <Link to="/" data-testid="article-back" className="inline-flex items-center gap-2 font-body text-sm text-bronze hover:text-ink transition-colors">
            <ArrowLeft size={16} /> Back to Home
          </Link>
        </Reveal>
        <Reveal delay={0.05}><Eyebrow className="mt-8 block">{article.publication}{article.date ? ` · ${article.date}` : ""}</Eyebrow></Reveal>
        <Reveal delay={0.1}><h1 className="mt-4 font-heading text-4xl md:text-5xl text-ink leading-tight">{article.title}</h1></Reveal>
        {article.excerpt && <Reveal delay={0.15}><p className="mt-6 font-accent text-2xl italic text-bronze">{article.excerpt}</p></Reveal>}
      </div>

      {article.image_url && (
        <Reveal delay={0.18}>
          <div className="max-w-4xl mx-auto px-6 md:px-12 mt-10">
            <div className="overflow-hidden rounded-[2rem] border border-champagne/30">
              <img src={article.image_url} alt={article.title} className="w-full object-cover" />
            </div>
          </div>
        </Reveal>
      )}

      <div className="max-w-3xl mx-auto px-6 md:px-12 mt-10">
        {article.embed_html && (
          <div className="mb-8 rounded-2xl overflow-hidden" dangerouslySetInnerHTML={{ __html: article.embed_html }} />
        )}
        {article.body && (
          <Reveal>
            <div
              className="blog-content font-body text-base md:text-lg text-ink/80 leading-relaxed"
              data-testid="article-body"
              dangerouslySetInnerHTML={{ __html: renderBody(article.body) }}
            />
          </Reveal>
        )}
        {article.external_url && (
          <div className="mt-10">
            <GhostButton href={article.external_url} testid="article-external-link">
              Read Full Article <ExternalLink size={15} />
            </GhostButton>
          </div>
        )}
      </div>
    </article>
  );
}
